import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import useSheetAnim from '../hooks/useSheetAnim';

/**
 * SheetDate — the app's ONE date field. There are no popup calendars in this PWA.
 *
 * Same argument as SheetSelect: AntD's DatePicker anchors a floating panel to the
 * input, which on a phone opens under the thumb, is clipped by whatever sheet the
 * form already sits in, and shares the field with a text caret so the keyboard
 * races the calendar. This is a field-shaped trigger plus a bottom sheet holding a
 * full-width calendar with 44px targets — no typing, no keyboard, nothing to clip.
 *
 * Three panels behind one header: tap the month label to jump to a month grid, tap
 * the year there to jump to a year grid. Two taps reaches any month in ±60 years,
 * which is what a tenancy running two years out or an IC birth date actually needs
 * — arrows alone would be dozens of taps.
 *
 * Props mirror AntD's DatePicker closely enough that call sites port over as-is:
 *   value (dayjs|null) / onChange(dayjs|null) / placeholder / disabled / allowClear
 *   format       — how the CLOSED field reads (default 'DD MMM YYYY'); the value
 *                  handed back is always a dayjs, so callers keep formatting for
 *                  the wire themselves
 *   disabledDate — (dayjs) => boolean, same signature as AntD's
 *   minDate / maxDate — dayjs bounds, folded into the same check
 *   presets      — extra quick chips, [{ label, value: dayjs | () => dayjs }].
 *                  "Today" is always offered and needs no config.
 *   footerHint   — small print under the calendar (e.g. "fills in from the period")
 *
 * Layering matches SheetSelect: portaled to <body> at 4900, raise `zIndex` when the
 * field lives inside a sheet that itself sits higher. Stay under 5000.
 */

const Z = 4900;
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEAR_SPAN = 60; // years either side of the anchor in the year grid

const Arrow = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points={dir === 'l' ? '15 5 8 12 15 19' : '9 5 16 12 9 19'} />
  </svg>
);
const CalIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2.5" /><path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

export default function SheetDate({
  value,
  onChange,
  placeholder = 'Select date',
  format = 'DD MMM YYYY',
  title,
  allowClear = true,
  disabled = false,
  disabledDate,
  minDate,
  maxDate,
  presets,
  footerHint,
  zIndex = Z,
  style,
}) {
  const [open, setOpen] = useState(false);

  const isBlocked = useMemo(() => (d) => {
    if (minDate && d.isBefore(dayjs(minDate).startOf('day'), 'day')) return true;
    if (maxDate && d.isAfter(dayjs(maxDate).startOf('day'), 'day')) return true;
    return typeof disabledDate === 'function' ? !!disabledDate(d) : false;
  }, [disabledDate, minDate, maxDate]);

  // Which month an EMPTY field opens on. Today, unless the bounds put today out of
  // range — a delivery date that can't precede a 2028 completion must not open on
  // this month with all 31 days struck through and no hint of where to go.
  const anchor = useMemo(() => {
    const t = dayjs().startOf('day');
    if (minDate && t.isBefore(dayjs(minDate), 'day')) return dayjs(minDate).startOf('day');
    if (maxDate && t.isAfter(dayjs(maxDate), 'day')) return dayjs(maxDate).startOf('day');
    return t;
  }, [minDate, maxDate]);

  const commit = (d) => {
    setOpen(false);
    onChange?.(d ? d.startOf('day') : null);
  };
  const clear = (e) => {
    e?.stopPropagation();
    onChange?.(null);
  };

  return (
    <>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true); } }}
        style={{
          height: 'var(--field-h)',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 12px',
          borderRadius: 12,
          border: '1px solid var(--border)',
          background: disabled ? 'var(--surface2)' : 'var(--surface)',
          color: value ? 'var(--text)' : 'var(--muted)',
          fontSize: 14,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.65 : 1,
          userSelect: 'none',
          ...style,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value ? dayjs(value).format(format) : placeholder}
        </span>
        {allowClear && value && !disabled && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear date"
            onClick={clear}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clear(e); } }}
            style={{ flex: 'none', width: 18, height: 18, borderRadius: 50, background: 'var(--chip)', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, lineHeight: 1 }}
          >
            ✕
          </span>
        )}
        <span style={{ flex: 'none', color: 'var(--muted)', display: 'flex' }}><CalIcon /></span>
      </div>

      <CalendarSheet
        open={open}
        onClose={() => setOpen(false)}
        title={title || placeholder}
        value={value ? dayjs(value) : null}
        anchor={anchor}
        onPick={commit}
        onClear={allowClear && value ? () => commit(null) : undefined}
        isBlocked={isBlocked}
        presets={presets}
        footerHint={footerHint}
        zIndex={zIndex}
      />
    </>
  );
}

function CalendarSheet({ open, onClose, title, value, anchor, onPick, onClear, isBlocked, presets, footerHint, zIndex }) {
  const { mounted, closing } = useSheetAnim(open);
  // Which panel the body shows. Opening always returns to 'day' — a sheet that
  // reopened on last time's year grid would read as broken.
  const [panel, setPanel] = useState('day');
  // The month on screen. Follows the selected value when there is one, so
  // reopening a filled field lands on that date rather than on today.
  const [cursor, setCursor] = useState(() => (value || anchor || dayjs()).startOf('month'));
  const touch = useRef(null);
  const yearScroller = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPanel('day');
    setCursor((value || anchor || dayjs()).startOf('month'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Centre the year grid on the year in view whenever it opens.
  useEffect(() => {
    if (panel !== 'year' || !yearScroller.current) return;
    const el = yearScroller.current.querySelector('[data-current="1"]');
    el?.scrollIntoView({ block: 'center' });
  }, [panel]);

  // 6 rows × 7 so the grid never changes height as months change — a calendar
  // that grows and shrinks pushes the footer around under the thumb.
  const days = useMemo(() => {
    const first = cursor.startOf('month').startOf('week');
    return Array.from({ length: 42 }, (_, i) => first.add(i, 'day'));
  }, [cursor]);

  if (!mounted) return null;

  const today = dayjs().startOf('day');
  const step = (n) => setCursor((c) => c.add(n, panel === 'day' ? 'month' : 'year'));

  // Horizontal swipe flips the month, the gesture a phone calendar is expected to
  // answer to. Vertical drags are left alone so the sheet still scrolls.
  const onTouchStart = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.6) step(dx < 0 ? 1 : -1);
  };

  const quick = [
    { label: 'Today', get: () => dayjs() },
    ...(presets || []).map((p) => ({ label: p.label, get: () => (typeof p.value === 'function' ? p.value() : dayjs(p.value)) })),
  ];

  const headLabel = panel === 'day' ? cursor.format('MMMM YYYY') : cursor.format('YYYY');

  return createPortal(
    <div
      onClick={onClose}
      className={`sheet-scrim${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`sheet-panel${closing ? ' is-closing' : ''}`}
        style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '92%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <div style={{ padding: '8px 0 2px', display: 'flex', justifyContent: 'center', flex: 'none' }}>
          <span style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--border)' }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 16px 10px', borderBottom: '1px solid var(--border)', flex: 'none' }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
          <div
            onClick={onClose}
            role="button"
            tabIndex={0}
            aria-label="Close"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose?.(); } }}
            style={{ width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)', flex: 'none' }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" /></svg>
          </div>
        </div>

        {/* month bar — the label is the way into the month / year panels */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '10px 12px 6px', flex: 'none' }}>
          <NavBtn onClick={() => step(-1)} label={panel === 'day' ? 'Previous month' : 'Previous year'}><Arrow dir="l" /></NavBtn>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setPanel((p) => (p === 'day' ? 'month' : p === 'month' ? 'year' : 'day'))}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPanel((p) => (p === 'day' ? 'month' : p === 'month' ? 'year' : 'day')); } }}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 38, borderRadius: 10, cursor: 'pointer', fontSize: 14.5, fontWeight: 600, color: panel === 'day' ? 'var(--text)' : 'var(--primary)' }}
          >
            {headLabel}
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: panel === 'day' ? 'none' : 'rotate(180deg)' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <NavBtn onClick={() => step(1)} label={panel === 'day' ? 'Next month' : 'Next year'}><Arrow dir="r" /></NavBtn>
        </div>

        <div className="scrx" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 12px 4px' }} ref={panel === 'year' ? yearScroller : undefined}>
          {panel === 'day' && (
            <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 2 }}>
                {WEEKDAYS.map((w, i) => (
                  <div key={w + i} style={{ textAlign: 'center', fontSize: 11.5, fontWeight: 600, color: 'var(--muted)', padding: '4px 0' }}>{w}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
                {days.map((d) => {
                  const outside = d.month() !== cursor.month();
                  const blocked = isBlocked(d);
                  const on = value && d.isSame(value, 'day');
                  const isToday = d.isSame(today, 'day');
                  return (
                    <div
                      key={d.valueOf()}
                      role="button"
                      aria-disabled={blocked || undefined}
                      aria-current={isToday ? 'date' : undefined}
                      onClick={() => !blocked && onPick(d)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3px 0', cursor: blocked ? 'not-allowed' : 'pointer' }}
                    >
                      <span style={{
                        width: 40, height: 40, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontVariantNumeric: 'tabular-nums',
                        fontWeight: on || isToday ? 700 : 500,
                        background: on ? 'var(--primary)' : 'transparent',
                        color: blocked ? 'var(--muted)' : on ? '#fff' : outside ? 'var(--muted)' : 'var(--text)',
                        opacity: blocked ? 0.32 : outside && !on ? 0.45 : 1,
                        border: isToday && !on ? '1.5px solid var(--primary)' : '1.5px solid transparent',
                        textDecoration: blocked ? 'line-through' : 'none',
                      }}>
                        {d.date()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {panel === 'month' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, padding: '6px 0 10px' }}>
              {MONTHS.map((m, i) => {
                const on = value && value.year() === cursor.year() && value.month() === i;
                return (
                  <div
                    key={m}
                    role="button"
                    onClick={() => { setCursor((c) => c.month(i)); setPanel('day'); }}
                    style={{
                      height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: on ? 700 : 500,
                      background: on ? 'var(--primary)' : 'var(--surface2)',
                      color: on ? '#fff' : 'var(--text)',
                      border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                    }}
                  >
                    {m}
                  </div>
                );
              })}
            </div>
          )}

          {panel === 'year' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, padding: '6px 0 10px' }}>
              {Array.from({ length: YEAR_SPAN * 2 + 1 }, (_, i) => cursor.year() - YEAR_SPAN + i).map((y) => {
                const on = value && value.year() === y;
                const cur = y === cursor.year();
                return (
                  <div
                    key={y}
                    data-current={cur ? '1' : undefined}
                    role="button"
                    onClick={() => { setCursor((c) => c.year(y)); setPanel('month'); }}
                    style={{
                      height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: 11, cursor: 'pointer', fontSize: 13.5,
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: on || cur ? 700 : 500,
                      background: on ? 'var(--primary)' : cur ? 'var(--chip)' : 'var(--surface2)',
                      color: on ? '#fff' : cur ? 'var(--primary)' : 'var(--text)',
                      border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                    }}
                  >
                    {y}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 16px calc(10px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none' }}>
          {footerHint && <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>{footerHint}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="scrx" style={{ display: 'flex', gap: 8, overflowX: 'auto', flex: 1, minWidth: 0 }}>
              {quick.map((p) => {
                const d = p.get();
                const blocked = isBlocked(d);
                return (
                  <div
                    key={p.label}
                    onClick={() => !blocked && onPick(d)}
                    style={{
                      flex: 'none', fontSize: 12.5, fontWeight: 600, padding: '7px 13px', borderRadius: 999,
                      whiteSpace: 'nowrap', cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.4 : 1,
                      background: 'var(--surface2)', color: 'var(--primary)', border: '1px solid var(--border)',
                    }}
                  >
                    {p.label}
                  </div>
                );
              })}
            </div>
            {onClear && (
              <span onClick={onClear} style={{ flex: 'none', fontSize: 13, fontWeight: 600, color: 'var(--bad)', cursor: 'pointer', padding: '6px 2px' }}>Clear</span>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const NavBtn = ({ onClick, label, children }) => (
  <div
    role="button"
    tabIndex={0}
    aria-label={label}
    onClick={onClick}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    style={{ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)', flex: 'none' }}
  >
    {children}
  </div>
);
